import { useEffect, useState, useCallback, useMemo } from "react";
import Axios from "../../../utils/axios";
import AnimationLogo from "../../../components/Layout/LoadingAnimation/AnimationLogo";
import { IGroup } from "../../../interfaces/models/IGroup";
import Input from "../../../components/Atomos/Inputs/Input";
import GroupIcon from "../../../components/ViewMode/GroupIconComponent";
import { uploadImage } from "../../../utils/UploadImage";

interface IErrorState {
  groupRequired: boolean;
  photoRequired: boolean;
  bannerRequired: boolean;
  logoRequired: boolean;
  errorMessage?: string;
}

const initialErrorState: IErrorState = {
  groupRequired: false,
  photoRequired: false,
  bannerRequired: false,
  logoRequired: false,
  errorMessage: '',
};

const AddGroupPhotos: React.FC = () => {
  const axios = useMemo(() => new Axios(), []);
  const [errorState, setErrorState] = useState<IErrorState>(initialErrorState);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [selectedBanner, setSelectedBanner] = useState<File | null>(null);
  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [bannerPreview, setBannerPreview] = useState<string>('');
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<IGroup[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredGroups, setFilteredGroups] = useState<IGroup[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; currentGroup: string } | null>(null);
  const [isDraggingPhoto, setIsDraggingPhoto] = useState(false);
  const [isDraggingBanner, setIsDraggingBanner] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.Get('./group');
      const data = response.data;
      if (data != null) {
        setGroups(data);
        setFilteredGroups(data);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  }, [axios]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter(group =>
        group.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredGroups(filtered);
    }
  }, [searchTerm, groups]);

  const uploadGroupPhotos = useCallback(async (photoUrl: string | null, bannerUrl: string | null, logoUrl: string | null, group: IGroup) => {
    try {
      console.log('Updating group photos in DB:', {
        groupId: group.id,
        groupTitle: group.title,
        photoUrl: photoUrl,
        bannerUrl: bannerUrl,
        logoUrl: logoUrl
      });

      const groupToUpdate: any = {
        ...group,
      };

      // Only update fields that have new values
      if (photoUrl !== null) {
        groupToUpdate.photo = photoUrl;
      }
      if (bannerUrl !== null) {
        groupToUpdate.bannerPhoto = bannerUrl;
      }
      if (logoUrl !== null) {
        groupToUpdate.logo = logoUrl;
      }
      
      const response = await axios.Put('./group', groupToUpdate);
      console.log('Group photos updated successfully:', response);
      
      return true;
    } catch (error: any) {
      console.error('Error updating group photos:', error);
      throw error;
    }
  }, [axios]);

  const validateFile = (file: File, fileType: 'photo' | 'banner' | 'logo'): boolean => {
    const isPNG = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    
    if (!isPNG) {
      setErrorState({
        ...errorState,
        [`${fileType}Required`]: true,
        errorMessage: 'Only PNG files are allowed'
      });
      return false;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit for PNG
      setErrorState({
        ...errorState,
        [`${fileType}Required`]: true,
        errorMessage: 'File size must be less than 10MB'
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = (file: File, fileType: 'photo' | 'banner' | 'logo') => {
    if (!validateFile(file, fileType)) {
      return;
    }

    if (fileType === 'photo') {
      setSelectedPhoto(file);
      setErrorState({ ...errorState, photoRequired: false, errorMessage: undefined });
    } else if (fileType === 'banner') {
      setSelectedBanner(file);
      setErrorState({ ...errorState, bannerRequired: false, errorMessage: undefined });
    } else {
      setSelectedLogo(file);
      setErrorState({ ...errorState, logoRequired: false, errorMessage: undefined });
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      if (fileType === 'photo') {
        setPhotoPreview(e.target?.result as string);
      } else if (fileType === 'banner') {
        setBannerPreview(e.target?.result as string);
      } else {
        setLogoPreview(e.target?.result as string);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent, fileType: 'photo' | 'banner' | 'logo') => {
    e.preventDefault();
    if (fileType === 'photo') {
      setIsDraggingPhoto(true);
    } else if (fileType === 'banner') {
      setIsDraggingBanner(true);
    } else {
      setIsDraggingLogo(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent, fileType: 'photo' | 'banner' | 'logo') => {
    e.preventDefault();
    if (fileType === 'photo') {
      setIsDraggingPhoto(false);
    } else if (fileType === 'banner') {
      setIsDraggingBanner(false);
    } else {
      setIsDraggingLogo(false);
    }
  };

  const handleDrop = (e: React.DragEvent, fileType: 'photo' | 'banner' | 'logo') => {
    e.preventDefault();
    if (fileType === 'photo') {
      setIsDraggingPhoto(false);
    } else if (fileType === 'banner') {
      setIsDraggingBanner(false);
    } else {
      setIsDraggingLogo(false);
    }

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0], fileType);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'photo' | 'banner' | 'logo') => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFileSelect(files[0], fileType);
    }
  };

  const handleRemoveFile = (fileType: 'photo' | 'banner' | 'logo') => {
    if (fileType === 'photo') {
      setSelectedPhoto(null);
      setPhotoPreview('');
      setErrorState({ ...errorState, photoRequired: false, errorMessage: undefined });
    } else if (fileType === 'banner') {
      setSelectedBanner(null);
      setBannerPreview('');
      setErrorState({ ...errorState, bannerRequired: false, errorMessage: undefined });
    } else {
      setSelectedLogo(null);
      setLogoPreview('');
      setErrorState({ ...errorState, logoRequired: false, errorMessage: undefined });
    }
  };

  const handleSubmit = useCallback(async () => {
    // At least one file must be selected
    const hasAtLeastOneFile = selectedPhoto || selectedBanner || selectedLogo;
    
    const errors = {
      ...initialErrorState,
      groupRequired: selectedGroups.length === 0,
      photoRequired: false,
      bannerRequired: false,
      logoRequired: false,
      errorMessage: !hasAtLeastOneFile ? 'Please select at least one file to upload' : undefined
    };

    setErrorState(errors);

    if (selectedGroups.length === 0 || !hasAtLeastOneFile) {
      return;
    }

    try {
      setUploading(true);
      setUploadProgress({ current: 0, total: selectedGroups.length, currentGroup: '' });
      
      // Sanitize filenames - remove special characters
      const sanitizeFileName = (fileName: string) => {
        return fileName
          .toLowerCase()
          .replace(/[^a-z0-9.]/g, '-') // Replace any non-alphanumeric (except dots) with hyphen
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
      };
      
      // Prepare files for upload (only if selected)
      const filesToUpload: Array<{ file: File; type: 'photo' | 'banner' | 'logo' }> = [];
      
      if (selectedPhoto) {
        const sanitizedPhotoName = sanitizeFileName(selectedPhoto.name);
        filesToUpload.push({
          file: new File([selectedPhoto], sanitizedPhotoName, { type: selectedPhoto.type }),
          type: 'photo'
        });
      }
      
      if (selectedBanner) {
        const sanitizedBannerName = sanitizeFileName(selectedBanner.name);
        filesToUpload.push({
          file: new File([selectedBanner], sanitizedBannerName, { type: selectedBanner.type }),
          type: 'banner'
        });
      }
      
      if (selectedLogo) {
        const sanitizedLogoName = sanitizeFileName(selectedLogo.name);
        filesToUpload.push({
          file: new File([selectedLogo], sanitizedLogoName, { type: selectedLogo.type }),
          type: 'logo'
        });
      }
      
      console.log('Uploading files to', selectedGroups.length, 'groups');
      
      let successCount = 0;
      let errorCount = 0;
      
      // Process each selected group
      for (let i = 0; i < selectedGroups.length; i++) {
        const group = selectedGroups[i];
        
        setUploadProgress({ 
          current: i + 1, 
          total: selectedGroups.length, 
          currentGroup: group.title 
        });
        
        try {
          console.log(`Uploading files to group ${i + 1}/${selectedGroups.length}: ${group.title}`);
          
          // Upload only selected files to Azure Storage for each group
          // Path: UAT/groups/[groupId]/[timestamp][sanitized-filename].png
          const uploadPromises = filesToUpload.map(({ file, type }) => 
            uploadImage(file, group.id!, 'groups/').then(url => ({ url, type }))
          );
          
          const uploadResults = await Promise.all(uploadPromises);
          
          // Extract URLs by type
          let photoUrl: string | null = null;
          let bannerUrl: string | null = null;
          let logoUrl: string | null = null;
          
          uploadResults.forEach(({ url, type }) => {
            if (url) {
              if (type === 'photo') {
                photoUrl = url;
              }
              else if (type === 'banner') {
                bannerUrl = url;
              }
              else if (type === 'logo') {
                logoUrl = url;
              }
            }
          });

          // Update group with uploaded files (preserve existing values for files not uploaded)
          await uploadGroupPhotos(photoUrl, bannerUrl, logoUrl, group);
          successCount++;
        } catch (error: any) {
          console.error(`Error uploading photos to group ${group.title}:`, error);
          errorCount++;
        }
      }
      
      setUploading(false);
      setUploadProgress(null);
      
      // Show success message
      if (successCount > 0) {
        setErrorState({
          ...initialErrorState,
          errorMessage: successCount === selectedGroups.length
            ? `✓ Successfully uploaded photos to ${successCount} group${successCount > 1 ? 's' : ''}`
            : `✓ Uploaded to ${successCount} group${successCount > 1 ? 's' : ''}, ${errorCount} error${errorCount > 1 ? 's' : ''}`
        });
        
        // Clear selection after successful upload
        setTimeout(() => {
          setSelectedGroups([]);
          setSearchTerm('');
          setSelectedPhoto(null);
          setSelectedBanner(null);
          setSelectedLogo(null);
          setPhotoPreview('');
          setBannerPreview('');
          setLogoPreview('');
          setErrorState(initialErrorState);
          
          // Refresh groups list
          fetchGroups();
        }, 2000);
      } else {
        setErrorState({
          ...initialErrorState,
          errorMessage: `Error uploading photos to all groups. Please try again.`
        });
      }
    } catch (error: any) {
      console.error('Error uploading photos:', error);
      setErrorState((prev) => ({
        ...prev,
        errorMessage: 'Error uploading photos. Please try again.'
      }));
      setUploading(false);
      setUploadProgress(null);
    }
  }, [selectedGroups, selectedPhoto, selectedBanner, selectedLogo, uploadGroupPhotos, fetchGroups]);

  return (
    <main className="mainSection p-8">
      {uploading && (
        <div className="absolute top-0 left-0">
          <AnimationLogo />
        </div>
      )}
      
      <div className="max-w-4xl mx-auto h-full">
        <h1 className="text-3xl font-bold text-gray-font mb-2">Add Group Photos</h1>
        <p className="text-gray-500 mb-8">Admin panel to upload photos and banners for groups</p>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Group
            </label>
            <Input
              onChange={(e) => setSearchTerm(e.target.value)}
              name="search"
              title="Search by group name"
              value={searchTerm}
              error={errorState.groupRequired}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Groups {errorState.groupRequired && <span className="text-feedback-error">*Required</span>}
              {selectedGroups.length > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({selectedGroups.length} selected)
                </span>
              )}
            </label>
            <div className="mb-2 flex gap-2">
              <button
                onClick={() => {
                  if (selectedGroups.length === filteredGroups.length) {
                    setSelectedGroups([]);
                  } else {
                    setSelectedGroups([...filteredGroups]);
                  }
                }}
                className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                {selectedGroups.length === filteredGroups.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {filteredGroups.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">No groups found</div>
              ) : (
                filteredGroups.map((group) => {
                  const isSelected = selectedGroups.some(g => g.id === group.id);
                  return (
                    <div
                      key={group.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedGroups(selectedGroups.filter(g => g.id !== group.id));
                        } else {
                          setSelectedGroups([...selectedGroups, group]);
                        }
                      }}
                      className={`p-4 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-4 ${
                        isSelected ? 'bg-primary-light' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            setSelectedGroups(selectedGroups.filter(g => g.id !== group.id));
                          } else {
                            setSelectedGroups([...selectedGroups, group]);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <div className="w-12 h-12 rounded-full bg-primary-light flex items-center justify-center">
                        {group.icon ? (
                          <GroupIcon groupId={group.id!} iconUrl={group.icon} />
                        ) : (
                          <span className="text-primary font-bold">{group.title.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{group.title}</div>
                        <div className="text-sm text-gray-500">
                          Type: {group.type} {group.regionName && `• ${group.regionName}`}
                        </div>
                      </div>
                      {(group.photo || group.bannerPhoto) && (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                          Has Photos
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {selectedGroups.length > 0 && (
            <>
              <div className="mb-6 p-4 bg-blue-50 rounded-md">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  Selected Groups ({selectedGroups.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedGroups.map((group) => (
                    <span
                      key={group.id}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {group.title}
                      <button
                        onClick={() => {
                          setSelectedGroups(selectedGroups.filter(g => g.id !== group.id));
                        }}
                        className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {/* Photo Upload */}
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Photo (PNG only)
                  </div>
                  
                  {!selectedPhoto ? (
                    <div
                      onDragOver={(e) => handleDragOver(e, 'photo')}
                      onDragLeave={(e) => handleDragLeave(e, 'photo')}
                      onDrop={(e) => handleDrop(e, 'photo')}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDraggingPhoto 
                          ? 'border-primary bg-primary-light' 
                          : 'border-gray-300 hover:border-primary hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="file"
                        id="photoInput"
                        accept=".png,image/png"
                        onChange={(e) => handleFileInputChange(e, 'photo')}
                        className="hidden"
                      />
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div>
                          <p className="text-base font-medium text-gray-700 mb-1">
                            {isDraggingPhoto ? 'Drop PNG file here' : 'Drag and drop PNG file'}
                          </p>
                          <p className="text-xs text-gray-500 mb-2">or</p>
                          <label
                            htmlFor="photoInput"
                            className="inline-block px-3 py-1.5 bg-primary text-white rounded-md cursor-pointer hover:bg-primary-dark transition-colors text-sm"
                          >
                            Browse Files
                          </label>
                        </div>
                        <p className="text-xs text-gray-400">PNG files only, max 10MB</p>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-16 h-16 border border-gray-300 rounded-lg bg-white p-1 flex items-center justify-center overflow-hidden">
                          {photoPreview && (
                            <img src={photoPreview} alt="Photo Preview" className="max-w-full max-h-full object-contain" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{selectedPhoto.name}</p>
                              <p className="text-xs text-gray-500">
                                {(selectedPhoto.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveFile('photo')}
                              className="text-red-500 hover:text-red-700 font-medium text-sm flex-shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-green-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium">Ready</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Banner Upload */}
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Banner (PNG only)
                  </div>
                  
                  {!selectedBanner ? (
                    <div
                      onDragOver={(e) => handleDragOver(e, 'banner')}
                      onDragLeave={(e) => handleDragLeave(e, 'banner')}
                      onDrop={(e) => handleDrop(e, 'banner')}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDraggingBanner 
                          ? 'border-primary bg-primary-light' 
                          : 'border-gray-300 hover:border-primary hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="file"
                        id="bannerInput"
                        accept=".png,image/png"
                        onChange={(e) => handleFileInputChange(e, 'banner')}
                        className="hidden"
                      />
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div>
                          <p className="text-base font-medium text-gray-700 mb-1">
                            {isDraggingBanner ? 'Drop PNG file here' : 'Drag and drop PNG file'}
                          </p>
                          <p className="text-xs text-gray-500 mb-2">or</p>
                          <label
                            htmlFor="bannerInput"
                            className="inline-block px-3 py-1.5 bg-primary text-white rounded-md cursor-pointer hover:bg-primary-dark transition-colors text-sm"
                          >
                            Browse Files
                          </label>
                        </div>
                        <p className="text-xs text-gray-400">PNG files only, max 10MB</p>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-16 h-16 border border-gray-300 rounded-lg bg-white p-1 flex items-center justify-center overflow-hidden">
                          {bannerPreview && (
                            <img src={bannerPreview} alt="Banner Preview" className="max-w-full max-h-full object-contain" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{selectedBanner.name}</p>
                              <p className="text-xs text-gray-500">
                                {(selectedBanner.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveFile('banner')}
                              className="text-red-500 hover:text-red-700 font-medium text-sm flex-shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-green-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium">Ready</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Logo Upload */}
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Logo (PNG only)
                  </div>
                  
                  {!selectedLogo ? (
                    <div
                      onDragOver={(e) => handleDragOver(e, 'logo')}
                      onDragLeave={(e) => handleDragLeave(e, 'logo')}
                      onDrop={(e) => handleDrop(e, 'logo')}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                        isDraggingLogo 
                          ? 'border-primary bg-primary-light' 
                          : 'border-gray-300 hover:border-primary hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="file"
                        id="logoInput"
                        accept=".png,image/png"
                        onChange={(e) => handleFileInputChange(e, 'logo')}
                        className="hidden"
                      />
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <div>
                          <p className="text-base font-medium text-gray-700 mb-1">
                            {isDraggingLogo ? 'Drop PNG file here' : 'Drag and drop PNG file'}
                          </p>
                          <p className="text-xs text-gray-500 mb-2">or</p>
                          <label
                            htmlFor="logoInput"
                            className="inline-block px-3 py-1.5 bg-primary text-white rounded-md cursor-pointer hover:bg-primary-dark transition-colors text-sm"
                          >
                            Browse Files
                          </label>
                        </div>
                        <p className="text-xs text-gray-400">PNG files only, max 10MB</p>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-green-500 rounded-lg p-4 bg-green-50">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-16 h-16 border border-gray-300 rounded-lg bg-white p-1 flex items-center justify-center overflow-hidden">
                          {logoPreview && (
                            <img src={logoPreview} alt="Logo Preview" className="max-w-full max-h-full object-contain" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{selectedLogo.name}</p>
                              <p className="text-xs text-gray-500">
                                {(selectedLogo.size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveFile('logo')}
                              className="text-red-500 hover:text-red-700 font-medium text-sm flex-shrink-0"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="mt-1 flex items-center gap-1 text-green-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-xs font-medium">Ready</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {uploadProgress && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-900">
                      Uploading to {uploadProgress.currentGroup}...
                    </p>
                    <p className="text-sm text-blue-700">
                      {uploadProgress.current} / {uploadProgress.total}
                    </p>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {errorState.errorMessage && (
                <div className={`mb-4 p-3 rounded-md ${
                  errorState.errorMessage.startsWith('✓')
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm ${
                    errorState.errorMessage.startsWith('✓')
                      ? 'text-green-800'
                      : 'text-red-800'
                  }`}>
                    {errorState.errorMessage}
                  </p>
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={uploading || (!selectedPhoto && !selectedBanner && !selectedLogo) || selectedGroups.length === 0}
                className={`w-full py-3 px-6 rounded-md font-semibold transition-colors ${
                  uploading || (!selectedPhoto && !selectedBanner && !selectedLogo) || selectedGroups.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {uploading 
                  ? `Uploading to ${selectedGroups.length} group${selectedGroups.length > 1 ? 's' : ''}...` 
                  : `Upload Photos to ${selectedGroups.length} Group${selectedGroups.length > 1 ? 's' : ''}`
                }
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default AddGroupPhotos;

